import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, SwipeableDrawer, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import type { OrganizationAttendee } from 'services/api';
import { formatPeopleDate } from '../formatPeopleDate';
import { AttendeeRolePill } from './AttendeeRolePill';
import { EngagementPill } from './EngagementPill';

const TEXT_COLOR = '#383838';
const DIVIDER_COLOR = '#DEDEDE';

function Label ({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Typography
      sx={{
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '10px',
        fontWeight: 600,
        lineHeight: '18px',
        textTransform: 'uppercase',
        color: TEXT_COLOR,
      }}
    >
      {children}
    </Typography>
  );
}

function Value ({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Typography
      sx={{
        fontFamily: '\'Poppins\', sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '21px',
        color: TEXT_COLOR,
      }}
    >
      {children}
    </Typography>
  );
}

export function AttendeeDetailBottomSheet ({
  open,
  attendee,
  onClose,
}: {
  open: boolean;
  attendee: OrganizationAttendee | null;
  onClose: () => void;
}): ReactElement {
  if (!attendee) return null as unknown as ReactElement;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'flex-start',
          pt: '14px',
          px: '20px',
          pb: '36px',
          backgroundColor: 'white',
        }}
      >
        <Box display="flex" justifyContent="flex-end" width="100%">
          <IconButton onClick={onClose} size="small" sx={{ p: 0 }}>
            <CloseIcon sx={{ fontSize: 19, color: TEXT_COLOR }} />
          </IconButton>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap="3px"
          alignItems="flex-start"
          width="100%"
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap="8px">
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
                color: TEXT_COLOR,
              }}
            >
              {attendee.firstName} {attendee.lastName}
            </Typography>
            {attendee.role ? <AttendeeRolePill role={attendee.role} /> : null}
          </Box>
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
              color: TEXT_COLOR,
            }}
          >
            {attendee.schoolEmail}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', height: '1px', backgroundColor: DIVIDER_COLOR }} />

        <Box display="flex" flexDirection="column" gap="4px" width="100%">
          <Label>ENGAGEMENT</Label>
          <Box><EngagementPill engagement={attendee.engagement} /></Box>
        </Box>

        <Box display="flex" gap="16px" width="100%">
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>RSVPS</Label>
            <Value>
              {attendee.rsvpCount}/{attendee.totalEvents}
              {attendee.totalEvents > 0
                ? ` · ${Math.round((attendee.rsvpCount / attendee.totalEvents) * 100)}%`
                : ''}
            </Value>
          </Box>
          <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap="4px">
            <Label>ATTENDED</Label>
            <Value>
              {attendee.attendedCount}/{attendee.totalEvents} · {Math.round(attendee.attendedPercent)}%
            </Value>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap="4px" width="100%">
          <Label>LAST ATTENDED</Label>
          <Value>{formatPeopleDate(attendee.lastAttended)}</Value>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            p: '15px',
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '24px',
              color: TEXT_COLOR,
              textDecoration: 'underline',
            }}
          >
            Close
          </Typography>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
}
